import checkIcon from '@iconify/icons-material-symbols/check';
import type { ElementType, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import type { PolymorphicProps } from '#pie/constants/interfaces';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../../icon/Icon';

export interface Props extends PropsWithChildren {
  step: number;
  active?: boolean;
  done?: boolean;
  disabled?: boolean;
}

export const StepperItem = <TComponent extends ElementType = 'button'>({
  active,
  as,
  children,
  className,
  done,
  step,
  onClick,
  disabled,
  ...props
}: PolymorphicProps<TComponent, Props>) => {
  const Component = as ?? 'button';
  const s = stepper({ active, disabled, done });
  const { t } = useTranslation('common');
  return (
    <Component
      type="button"
      className={cn(s.base(), className)}
      disabled={active || !onClick}
      onClick={!disabled ? onClick : undefined}
      aria-label={`${t('stepper.step.aria-label')} ${step}`}
      {...props}
    >
      <span className={s.ball()}>
        {done ? (
          <Icon
            icon={checkIcon}
            className={s.icon()}
            aria-label={t('stepper.done.aria-label')}
          />
        ) : (
          step
        )}
      </span>
      {children}
    </Component>
  );
};

const stepper = tv({
  base: 'flex items-center gap-2 font-semibold text-content-tertiary group flex-shrink-0 text-sm align-middle',
  slots: {
    ball: 'h-7 w-7 rounded-full border border-border-minimal-enabled font-semibold justify-center items-center flex',
    icon: 'h-4 w-4',
  },
  variants: {
    active: {
      true: {
        ball: 'border-action-secondary-hover ring-2 ring-surface-subtle-teal group-hover:border-action-secondary-hover bg-white',
        base: 'text-content-body',
      },
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
    done: {
      true: {
        ball: 'bg-action-secondary-enabled border-action-secondary-hover text-content-reversed group-hover:border-action-secondary-hover group-hover:text-content-reversed',
      },
    },
  },
});
